import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormValidation } from '@rjsf/utils'
import { getUiSchema } from './uiSchemaService'
import { SettingsPopup } from '../SettingsPopup'
import { generateBotName, getInitialFormData, schema } from './utils'
import { ModalDescriptorForType } from '../../../store/modals'
import { deriveBotIdFromName, deriveProdBotIdFromDevBotId } from '../../../lib/bot'
import { useSettingsPopupState } from '../hooks'
import { modalsActions } from '../../../store/modals/modalsSlice'
import { endpointHelpers } from '../../../features/serverBots/endpointHelpers'
import { openBotIdSelector } from '../../../store/app/selectors'
import { LoadingSettingsPopup } from '../LoadingSettingsPopup'
import { SettingsPopupWithError } from '../SettingsPopupWithError'
import { switchBot } from '../../../lib/navigation'

interface Props {
  modalDescriptor: ModalDescriptorForType<'create_bot_settings'>
}

export const CreateBotSettingsPopup: React.FC<Props> = React.memo<Props>(({ modalDescriptor }) => {
  const dispatch = useDispatch()

  const creationType = modalDescriptor?.creationType
  const openSection = modalDescriptor?.openSection ?? 'form'
  const currentBotId = useSelector(openBotIdSelector)

  const [duplicateBot, { isLoading: isDuplicationLoading }] =
    endpointHelpers.duplicateCurrentBotToNewBot.useMutation()

  const [createNewBot, { isLoading: isCreationLoading }] =
    endpointHelpers.createNewBot.useMutation()

  const metadataQueryResult = endpointHelpers.getBotsMetadata.useQuery()

  const { initialFormData, formData, onFormDataChange } = useSettingsPopupState(
    () => getInitialFormData(openSection),
    [openSection]
  )

  const isLoading =
    metadataQueryResult.isLoading ||
    metadataQueryResult.isUninitialized ||
    isDuplicationLoading ||
    isCreationLoading

  if (isLoading) {
    return <LoadingSettingsPopup onClose={() => dispatch(modalsActions.closeModal())} />
  }

  if (metadataQueryResult.isError) {
    return (
      <SettingsPopupWithError
        onClose={() => dispatch(modalsActions.closeModal())}
        retry={() => metadataQueryResult.refetch()}
      />
    )
  }

  const { existingBotIds, allReservedIds } = metadataQueryResult?.data

  return (
    <SettingsPopup
      openSection={openSection}
      schema={schema}
      uiSchema={getUiSchema()}
      className="no-error-panel"
      close={() => dispatch(modalsActions.closeModal())}
      save={async formData => {
        const name = generateBotName(formData)
        const id = deriveBotIdFromName(name)
        const categorization = { ...formData, name }

        creationType === 'create_new' // I <3 PRETTIER
          ? await createNewBot({ id, categorization })
          : await duplicateBot({
              newBotId: id,
              categorization,
              versionTitle: `Duplicated from bot ${currentBotId}`
            })

        dispatch(modalsActions.closeModal())
        switchBot(id)
      }}
      formData={
        openSection === 'json'
          ? formData
          : { ...formData, expectedId: getExpectedIdFromFormData(formData) }
      }
      validationErrorsByField={{}}
      initialFormData={initialFormData}
      validateSchema={validateSchema({ existingBotIds, reservedBotIds: allReservedIds })}
      onChange={(newFormData, isInitialPopulation) => {
        if (openSection === 'form') {
          onFormDataChange(newFormData, isInitialPopulation)
        }
      }}
    />
  )
})

const validateSchema =
  (params: { existingBotIds: string[]; reservedBotIds: string[] }) =>
  (formData: any, errors: FormValidation): FormValidation => {
    const expectedBotId = formData.expectedId

    if (!expectedBotId) {
      return errors
    }

    if (params.existingBotIds?.some(existingBotId => existingBotId === expectedBotId)) {
      errors.addError('The bot with the given name already exists; please try a different name.')
      return errors
    }

    if (params.reservedBotIds?.some(reservedDevBotId => reservedDevBotId === expectedBotId)) {
      const prodBotId = deriveProdBotIdFromDevBotId(expectedBotId)
      errors.addError(
        `The bot with the given name is reserved for DEV bot of existing bot "${prodBotId}"; please try a different name.`
      )
      return errors
    }

    return errors
  }

const getExpectedIdFromFormData = (formData: any) => {
  const expectedName = generateBotName(formData)
  return deriveBotIdFromName(expectedName)
}
